import { ProofOfAddress } from "./documents/ProofOfAddress.tsx";
import type { Folder, Requirement } from "../../redux/interfaces/folder.types.ts";
import { ID } from "./documents/ID.tsx";
import { ProofOfIncome } from "./documents/ProofOfIncome.tsx";
import { GeneralForm } from "./forms/GeneralForm.tsx";
import { BdcCompanyDataStep } from "./forms/BdcCompanyDataStep.tsx";
import { SATOpinion } from "./documents/SatOpinion.tsx";
import { BdcAuthorizationNative } from "./documents/bdc/BdcAuthorizationNative.tsx";
import { DigitalSignature } from "./documents/DigitalSignature.tsx";
import { BoletaRegistral } from "./documents/BoletaRegistral.tsx";
import { Predial } from "./documents/Predial.tsx";
import { PowerOfAttorney } from "./documents/PowerOfAttorney.tsx";
import { BusinessAct } from "./documents/BusinessAct.tsx";
import { GeneralRequirement } from "./GeneralRequirement.tsx";
import { IDValidationErrorBoundary } from "./documents/id-validation";
import type {
  IdentityValidationActivity,
  IdentityValidationActivityUpdate,
} from "./identityValidationActivity";
import { isBdcAuthorizationRequirement } from "./utils/requirementGrouping";
import type { DataValidationNavigationOptions } from "./utils/bdcRecovery";

/**
 * Options passed to documentationMap for dynamic component configuration
 */
export interface DocumentationMapOptions {
  /** Revisit the INE address question after deliberate backward navigation. */
  reviewAddressOnEntry?: boolean;
  /** Callback when ID validation flow completes */
  onIDValidationComplete?: (isComplete: boolean) => void;
  /** Local upload/validation state, used before the folder cache catches up. */
  onIDValidationActivityChange?: (
    update: IdentityValidationActivityUpdate
  ) => void;
  identityValidationActivity?: IdentityValidationActivity;
  /** Authoritative prerequisite gate used by programmatic ID navigation. */
  isIDCompleted?: boolean;
  /** Callback to navigate to proof of address requirement */
  onNavigateToProofOfAddress?: () => void;
  /** Callback to navigate to data validation step (after INE address confirmed as updated) */
  onNavigateToDataValidation?: (
    options?: DataValidationNavigationOptions,
  ) => boolean;
  /** Callback to register the ID submit function for external triggering (e.g., from "Siguiente" button) */
  onRegisterSubmit?: (submitFn: () => Promise<boolean>) => void;
  /** Callback to register the ProofOfAddress submit function for external triggering */
  onRegisterProofOfAddressSubmit?: (submitFn: () => Promise<boolean>) => void;
  /** Callback to register the general submit function for forms/documents (triggered from "Siguiente" button) */
  onRegisterGeneralSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
  /** Callback to hide/show navigation footer (e.g., during validation flow sub-steps) */
  onSetNavigationHidden?: (hidden: boolean) => void;
  /** Callback when address confirmation result is received from ID validation */
  onAddressConfirmationResult?: (isUpdated: boolean) => void;
  /** Callback when ProofOfAddress validation flow completes */
  onProofOfAddressComplete?: (isComplete: boolean) => void;
  canContinueAfterProofOfAddress?: boolean;
  onNavigateToRequirement?: (
    requirement: Requirement,
    subfolderId: string,
    options?: { forceBdcCompanyDataEdit?: boolean },
  ) => void;
  forceBdcCompanyDataEdit?: boolean;
  onBdcCompanyDataEditRequestConsumed?: () => void;
  /**
   * When true, OCR-verified fields in GeneralForm render as editable inputs
   * instead of the read-only LockedFieldsSummary. Set from the admin mount
   * (FormView); actor mount leaves it undefined so the pathname fallback
   * takes over.
   */
  isAdminContext?: boolean;
}

// Helper to get the ID component with callbacks
const getID = (
  type: "tenant" | "guarantor" | "legal_rep" | "landlord" | "owner_rep",
  options?: DocumentationMapOptions
) => (
  <ID
    type={type}
    onIDValidationComplete={options?.onIDValidationComplete}
    onIDValidationActivityChange={options?.onIDValidationActivityChange}
    identityValidationActivity={options?.identityValidationActivity}
    isIDCompleted={options?.isIDCompleted}
    onNavigateToProofOfAddress={options?.onNavigateToProofOfAddress}
    onNavigateToDataValidation={options?.onNavigateToDataValidation}
    onRegisterSubmit={options?.onRegisterSubmit}
    onSetNavigationHidden={options?.onSetNavigationHidden}
    onAddressConfirmationResult={options?.onAddressConfirmationResult}
    reviewAddressOnEntry={options?.reviewAddressOnEntry}
  />
);

const getProofOfAddress = (
  type: "tenant" | "guarantor" | "company",
  options?: DocumentationMapOptions
) => (
  <IDValidationErrorBoundary documentType="proof-of-address">
    <ProofOfAddress
      type={type}
      onRegisterSubmit={options?.onRegisterProofOfAddressSubmit}
      onSetNavigationHidden={options?.onSetNavigationHidden}
      onProofOfAddressComplete={options?.onProofOfAddressComplete}
      canContinue={options?.canContinueAfterProofOfAddress}
    />
  </IDValidationErrorBoundary>
);

const getGeneralForm = (
  header: React.ReactNode,
  options?: DocumentationMapOptions
) => (
  <GeneralForm
    header={header}
    onRegisterSubmit={options?.onRegisterGeneralSubmit}
    isAdminContext={options?.isAdminContext}
  />
);

const getBdcCompanyDataForm = (options?: DocumentationMapOptions) => (
  <BdcCompanyDataStep
    header={
      <label>
        Llena los datos de la <b>empresa</b> para la consulta de Buró de Crédito
      </label>
    }
    onRegisterSubmit={options?.onRegisterGeneralSubmit}
    isAdminContext={options?.isAdminContext}
    forceEdit={options?.forceBdcCompanyDataEdit}
    onForceEditConsumed={options?.onBdcCompanyDataEditRequestConsumed}
  />
);

const getProofOfIncome = (
  type: "tenant" | "company" | "guarantor",
  options?: DocumentationMapOptions
) => (
  <ProofOfIncome type={type} onRegisterSubmit={options?.onRegisterGeneralSubmit} />
);

const getBoletaRegistral = (options?: DocumentationMapOptions): React.ReactNode => (
  <BoletaRegistral onRegisterSubmit={options?.onRegisterGeneralSubmit} />
);

const getSATOpinion = (options?: DocumentationMapOptions): React.ReactNode => (
  <SATOpinion onRegisterSubmit={options?.onRegisterGeneralSubmit} />
);

const getBusinessAct = (options?: DocumentationMapOptions): React.ReactNode => (
  <BusinessAct onRegisterSubmit={options?.onRegisterGeneralSubmit} />
);

const getPowerOfAttorney = (options?: DocumentationMapOptions): React.ReactNode => (
  <PowerOfAttorney onRegisterSubmit={options?.onRegisterGeneralSubmit} />
);

const getPredial = (
  type: "collateral" | "property",
  doc: "predial" | "scriptures",
  options?: DocumentationMapOptions
): React.ReactNode => (
  <Predial type={type} doc={doc} onRegisterSubmit={options?.onRegisterGeneralSubmit} />
);

const getBdcAuthorization = (
  folder: Folder,
  options?: DocumentationMapOptions,
): React.ReactNode => (
  <BdcAuthorizationNative
    folder={folder}
    onRegisterSubmit={options?.onRegisterGeneralSubmit}
    onSetNavigationHidden={options?.onSetNavigationHidden}
    onNavigateToRequirement={options?.onNavigateToRequirement}
    onNavigateToDataValidation={options?.onNavigateToDataValidation}
    isAdminContext={options?.isAdminContext}
  />
);

const getDigitalSignature = (folder: Folder): React.ReactNode => (
  <DigitalSignature folderId={folder.id} />
);

interface DocumentationMap {
  component: React.ReactNode;
  title?: string;
}

export const documentationMap = (
  requirementSlug: string,
  subfolderSlug: string,
  folder: Folder,
  options?: DocumentationMapOptions,
): DocumentationMap | null => {
  const map: Record<string, Record<string, React.ReactNode>> = {
    "tenant-pf": {
      "official-id": getID("tenant", options),
      "proof-of-income": getProofOfIncome("tenant", options),
      "proof-of-address": getProofOfAddress("tenant", options),
      "personal-reference-1": getGeneralForm(
        <label>
          Agrega los datos de una referencia personal.{" "}
          <b>
            NO PUEDE SER LA MISMA PERSONA QUE LA REFERENCIA LABORAL, EL
            FIADOR U OBLIGADO SOLIDARIO
          </b>
        </label>,
        options,
      ),
      "personal-reference-2": getGeneralForm(
        <label>
          Agrega los datos de una referencia personal.{" "}
          <b>
            NO PUEDE SER LA MISMA PERSONA QUE LA REFERENCIA LABORAL, EL
            FIADOR U OBLIGADO SOLIDARIO
          </b>
        </label>,
        options,
      ),
      "digital-signature": getDigitalSignature(folder),
      "bdc-authorization": getBdcAuthorization(folder, options),
      "tenant-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del <b>inquilino</b>
        </label>,
        options,
      ),
    },
    "tenant-pm": {
      "official-id": getID("legal_rep", options),
      "proof-of-income": getProofOfIncome("company", options),
      "proof-of-address": getProofOfAddress("company", options),
      "commercial-reference-1": getGeneralForm(
        <label>
          Agrega los datos de una referencia comercial.{" "}
          <b>
            NO PUEDE SER LA MISMA PERSONA QUE LA REFERENCIA LABORAL, EL
            FIADOR U OBLIGADO SOLIDARIO
          </b>
        </label>,
        options,
      ),
      "commercial-reference-2": getGeneralForm(
        <label>
          Agrega los datos de una referencia comercial.{" "}
          <b>
            NO PUEDE SER LA MISMA PERSONA QUE LA REFERENCIA LABORAL, EL
            FIADOR U OBLIGADO SOLIDARIO
          </b>
        </label>,
        options,
      ),
      "constitutive-act": getBusinessAct(options),
      "sat-compliance": getSATOpinion(options),
      "digital-signature": getDigitalSignature(folder),
      "bdc-company-data": getBdcCompanyDataForm(options),
      "bdc-authorization": getBdcAuthorization(folder, options),
      "power-of-attorney": getPowerOfAttorney(options),
      "business-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "legal-rep-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
    },
    "tenant-pf-cja": {
      "official-id": getID("tenant", options),
      "proof-of-income": getProofOfIncome("tenant", options),
      "tenant-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del <b>inquilino</b>
        </label>,
        options,
      ),
    },
    "tenant-pm-cja": {
      "official-id": getID("legal_rep", options),
      "proof-of-income": getProofOfIncome("company", options),
      "constitutive-act": getBusinessAct(options),
      "sat-compliance": getSATOpinion(options),
      "power-of-attorney": getPowerOfAttorney(options),
      "business-cja-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "legal-rep-short-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
    },
    "guarantor-pf": {
      "official-id": getID("guarantor", options),
      "proof-of-income": getProofOfIncome("guarantor", options),
      "proof-of-address": getProofOfAddress("guarantor", options),
      "guarantor-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>obligado solidario</b>
        </label>,
        options,
      ),
      "bdc-authorization": getBdcAuthorization(folder, options),
    },
    "guarantor-pf-cja": {
      "official-id": getID("guarantor", options),
      "proof-of-income": getProofOfIncome("guarantor", options),
      "proof-of-address": getProofOfAddress("guarantor", options),
      "guarantor-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>obligado solidario</b>
        </label>,
        options,
      ),
      "bdc-authorization": getBdcAuthorization(folder, options),
    },
    "guarantor-investigation-pf": {
      "official-id": getID("guarantor", options),
      "proof-of-income": getProofOfIncome("guarantor", options),
      "proof-of-address": getProofOfAddress("guarantor", options),
      "guarantor-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>obligado solidario</b>
        </label>,
        options,
      ),
      "bdc-authorization": getBdcAuthorization(folder, options),
    },
    "guarantor-pm": {
      "official-id": getID("legal_rep", options),
      "proof-of-income": getProofOfIncome("guarantor", options),
      "proof-of-address": getProofOfAddress("company", options),
      "constitutive-act": getBusinessAct(options),
      "business-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "power-of-attorney": getPowerOfAttorney(options),
      "bdc-company-data": getBdcCompanyDataForm(options),
      "bdc-authorization": getBdcAuthorization(folder, options),
      "legal-rep-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
    },
    "guarantor-pm-cja": {
      "official-id": getID("legal_rep", options),
      "proof-of-address": getProofOfAddress("company", options),
      "proof-of-income": getProofOfIncome("guarantor", options),
      "constitutive-act": getBusinessAct(options),
      "business-short-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "power-of-attorney": getPowerOfAttorney(options),
      "legal-rep-short-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
    },
    "guarantor-investigation-pm": {
      "official-id": getID("legal_rep", options),
      "proof-of-address": getProofOfAddress("company", options),
      "proof-of-income": getProofOfIncome("guarantor", options),
      "constitutive-act": getBusinessAct(options),
      "business-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "power-of-attorney": getPowerOfAttorney(options),
      "bdc-company-data": getBdcCompanyDataForm(options),
      "bdc-authorization": getBdcAuthorization(folder, options),
      "legal-rep-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
    },
    collateral: {
      predial: getPredial("collateral", "predial", options),
      "boleta-registral": getBoletaRegistral(options),
    },
    "lease-contract-pf": {
      "official-id": getID("landlord", options),
      "landlord-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del <b>propietario</b>
        </label>,
        options,
      ),
      "lease-contract-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la{" "}
          <b>renta y el contrato</b>
        </label>,
        options,
      ),
      "escrituras-predial": getPredial("property", "scriptures", options),
    },
    "lease-contract-pm": {
      "official-id": getID("legal_rep", options),
      "legal-rep-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
      "business-form-lease-contract": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "lease-contract-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la{" "}
          <b>renta y el contrato</b>
        </label>,
        options,
      ),
      "escrituras-predial": getPredial("property", "scriptures", options),
      "constitutive-act": getBusinessAct(options),
      "power-of-attorney": getPowerOfAttorney(options),
      "sat-compliance": getSATOpinion(options),
    },
    // MB-431: identity-only templates for secondary landlords. Mirror the official-id
    // ID-capture + landlord forms used by lease-contract-pf/pm so they get the same UI.
    "landlord-pf": {
      "official-id": getID("landlord", options),
      "landlord-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del <b>propietario</b>
        </label>,
        options,
      ),
    },
    "landlord-pm": {
      "official-id": getID("legal_rep", options),
      "constitutive-act": getBusinessAct(options),
      "power-of-attorney": getPowerOfAttorney(options),
      "business-short-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "legal-rep-short-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
    },
    "owner-representative": {
      "official-id": getID("owner_rep", options),
      "owner-representative-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante del propietario</b>
        </label>,
        options,
      ),
    },
    // MB-431: PM owner's representative (legal entity). Same identity docs as landlord-pm.
    "owner-representative-pm": {
      "official-id": getID("legal_rep", options),
      "constitutive-act": getBusinessAct(options),
      "power-of-attorney": getPowerOfAttorney(options),
      "business-short-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos de la <b>empresa</b>
        </label>,
        options,
      ),
      "legal-rep-short-form": getGeneralForm(
        <label>
          Llena el siguiente formulario con los datos del{" "}
          <b>representante legal</b>
        </label>,
        options,
      ),
    },
  };

  if (!map[subfolderSlug]) return { component: <GeneralRequirement onRegisterSubmit={options?.onRegisterGeneralSubmit} /> };

  const mappedRequirementSlug = isBdcAuthorizationRequirement(
    requirementSlug,
    requirementSlug,
  )
    ? "bdc-authorization"
    : requirementSlug;
  const requirement = map[subfolderSlug][mappedRequirementSlug];
  return {
    component: requirement || <GeneralRequirement onRegisterSubmit={options?.onRegisterGeneralSubmit} />,
  };
};
